<script setup>
import { WdsButtonGhost, WdsIllustration, WdsSkeleton } from '@wds/components';
import { APPDOWNLOADBANNERLINKS } from '@/config/app-download-banner-links';
import { homepageConfigs } from '@/config/homepage';
import { PROPERTY_TYPE_DEFAULT } from '@/config/property-types';

// ### COMPOSABLES
const nuxtApp = useNuxtApp();
const { $tracking } = nuxtApp;
const {
  locale,
  t,
  defaultLocaleObj,
} = useI18n();
const { useFormatter } = useFormatters();
const { useUrl } = useUrls();

// ### COMPOSABLES - APIs
const { getCityById } = useApiCities();
const { getSocialStats } = useApiTravelerProfile();

// ### CONSTANTS
const mostBookedCityId = homepageConfigs.SOCIAL_HIGHLIGHTS.MOST_BOOKED_CITY_ID;

// ### VARIABLES INITIALIZATION
const mostBookedCityInfo = ref(null);
const socialStats = ref({
  travellers_in_hostels: null,
  travellers_in_chat: null,
  travellers_in_events: null,
});

const mostBookedCityLink = computed(() => {
  if (!mostBookedCityInfo.value) {
    return '';
  }

  return useUrl.getCityPageUrl({
    propertyTypeSlug: t(PROPERTY_TYPE_DEFAULT.slug),
    urlFriendlyContinent: mostBookedCityInfo.value?.urlFriendlyContinent,
    urlFriendlyCountry: mostBookedCityInfo.value?.urlFriendlyCountry,
    urlFriendlyCity: mostBookedCityInfo.value?.urlFriendlyName,
  });
});

// ### METHODS
function hasSocialHighlightsStat(stat) {
  return socialStats.value[stat] !== null;
}

function formatTravellerNumbers(num) {
  if (!Number.isInteger(num)) {
    return '-';
  }

  const localeObject = useFormatter.getLocaleObjectByCode(locale.value) || defaultLocaleObj;

  return Intl.NumberFormat(localeObject.language).format(num);
}

function triggerSocialHighlightClick(socialHighlightClicked) {
  let trackingInfo = {};

  switch (socialHighlightClicked) {
    case 'most_booked_city_id':
      trackingInfo = {
        action: 'Social Stats Section Clicked',
        social_stats_section_clicked: 'Most Booked City',
        social_stats_section_content: mostBookedCityInfo.value?.tracking?.name,
      };
      break;
    case 'travellers_in_hostels':
      trackingInfo = {
        action: 'Social Stats Section Clicked',
        social_stats_section_clicked: 'Hostel Travellers',
        social_stats_section_content: socialStats.value?.travellers_in_hostels,
      };
      break;
    case 'travellers_in_chat':
      trackingInfo = {
        action: 'Social Stats Section Clicked',
        social_stats_section_clicked: 'Chat Travellers',
        social_stats_section_content: socialStats.value?.travellers_in_chat,
      };
      break;
    case 'travellers_in_events':
      trackingInfo = {
        action: 'Social Stats Section Clicked',
        social_stats_section_clicked: 'Event Joiners',
        social_stats_section_content: socialStats.value?.travellers_in_events,
      };
      break;
    case 'join_in_app':
      trackingInfo = { action: 'Join Them On App Clicked' };
      break;
    default:
      break;
  }

  if (trackingInfo?.action) {
    $tracking.triggerSocialHighlightsEventAction(trackingInfo);
  }
}

onMounted(async () => {
  getSocialStats().then((response) => {
    socialStats.value = {
      travellers_in_hostels: response?.travellers_in_hostels ?? '-',
      travellers_in_chat: response?.travellers_in_chat ?? '-',
      travellers_in_events: response?.travellers_in_events ?? '-',
    };
  });

  if (Number.isInteger(mostBookedCityId)) {
    getCityById(mostBookedCityId).then((response) => {
      mostBookedCityInfo.value = response;
    });
  }
});
</script>

<template>
  <section class="social-highlights-container">
    <component
      :is="mostBookedCityLink ? 'a' : 'div'"
      :href="mostBookedCityLink"
      class="social-highlight-tile most-booked"
      :title="
        t('t_A11YBCTITLELOCATION', {
          LOCATIONNAME: mostBookedCityInfo?.name,
          PROPERTYTYPE: t(PROPERTY_TYPE_DEFAULT?.transcode),
        })
      "
      @click="triggerSocialHighlightClick('most_booked_city_id')"
    >
      <WdsIllustration
        class="sh-tile-icon"
        asset="fire"
      />
      <span class="sh-tile-title">{{ $t('t_SOCIALHIGHLIGHTSMOSTBOOKEDTODAY') }}</span>
      <span class="sh-tile-value">
        <template v-if="mostBookedCityInfo?.name">
          {{ mostBookedCityInfo?.name }}
        </template>
        <WdsSkeleton
          v-else
          height="32px"
          width="80%"
        />
      </span>
    </component>
    <div
      class="social-highlight-tile travellers-staying"
      @click="triggerSocialHighlightClick('travellers_in_hostels')"
    >
      <WdsIllustration
        class="sh-tile-icon"
        asset="backpack"
      />
      <span class="sh-tile-title">{{ $t('t_SOCIALHIGHLIGHTSTRAVELLERSINHOSTELS') }}</span>
      <span class="sh-tile-value">
        <template v-if="hasSocialHighlightsStat('travellers_in_hostels')">
          {{ formatTravellerNumbers(socialStats?.travellers_in_hostels) }}
        </template>
        <WdsSkeleton
          v-else
          height="32px"
          width="80%"
        />
      </span>
    </div>
    <div class="social-highlight-tile social-members">
      <WdsIllustration
        class="sh-tile-icon"
        asset="sunglasses"
      />
      <span class="sh-tile-title">{{ $t('t_SOCIALHIGHLIGHTSSOCIALMEMBERS') }}</span>
      <span class="sh-tile-value">
        <template v-if="mostBookedCityInfo?.name"> {{ $t('t_SOCIALHIGHLIGHTSSOCIALMEMBERSNUM') }} </template>
        <WdsSkeleton
          v-else
          height="32px"
          width="80%"
        />
      </span>
    </div>
    <div
      class="social-highlight-tile travellers-in-chat"
      @click="triggerSocialHighlightClick('travellers_in_chat')"
    >
      <WdsIllustration
        class="sh-tile-icon"
        asset="communication"
      />
      <span class="sh-tile-title">{{ $t('t_SOCIALHIGHLIGHTSTRAVELLERSINCHATS') }}</span>
      <span class="sh-tile-value">
        <template v-if="hasSocialHighlightsStat('travellers_in_chat')">
          {{ formatTravellerNumbers(socialStats?.travellers_in_chat) }}
        </template>
        <WdsSkeleton
          v-else
          height="32px"
          width="80%"
        />
      </span>
    </div>
    <div
      class="social-highlight-tile travellers-in-events"
      @click="triggerSocialHighlightClick('travellers_in_events')"
    >
      <WdsIllustration
        class="sh-tile-icon"
        asset="hifive"
      />
      <span class="sh-tile-title">{{ $t('t_SOCIALHIGHLIGHTSTRAVELLERSINEVENTS') }}</span>
      <span class="sh-tile-value">
        <template v-if="hasSocialHighlightsStat('travellers_in_events')">
          {{ formatTravellerNumbers(socialStats?.travellers_in_events) }}
        </template>
        <WdsSkeleton
          v-else
          height="32px"
          width="80%"
        />
      </span>
    </div>
    <WdsButtonGhost
      :href="APPDOWNLOADBANNERLINKS.SOCIALHIGHLIGHTS"
      class="social-highlight-button"
      :text="$t('t_SOCIALHIGHLIGHTSJOININAPP')"
      icon-end="arrow-right"
      @click="triggerSocialHighlightClick('join_in_app')"
    />
  </section>
</template>

<style scoped lang="scss">
@mixin inline-tile {
  gap: 0 $wds-spacing-s;
  grid-template-columns: wds-rem(40px) 1fr;
  grid-template-rows: repeat(2, min-content);

  .sh-tile-icon {
    grid-column: 1;
    grid-row: 1 / span 2;
    align-self: center;
    margin-bottom: 0;
    width: wds-rem(40px);
    height: wds-rem(40px);
  }

  .sh-tile-title {
    grid-column: 2;
    grid-row: 1;
  }

  .sh-tile-value {
    grid-column: 2;
    grid-row: 2;
  }
}

@mixin block-tile {
  grid-template-columns: 1fr;
  grid-template-rows: repeat(3, min-content);

  .sh-tile-icon {
    grid-column: auto;
    grid-row: auto;
    align-self: center;
    margin-bottom: $wds-spacing-s;
    width: wds-rem(40px);
    height: wds-rem(40px);
  }

  .sh-tile-title {
    grid-column: auto;
    grid-row: auto;
  }

  .sh-tile-value {
    grid-column: auto;
    grid-row: auto;
  }
}

.social-highlights-container {
  display: grid;
  gap: $wds-spacing-s;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr min-content 1fr min-content;

  .social-highlight-tile {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(3, min-content);
    padding: $wds-spacing-s $wds-spacing-m;
    border-radius: $wds-border-radius-xl;
    min-height: wds-rem(104px);
    align-content: center;

    .sh-tile-icon {
      width: wds-rem(40px);
      height: wds-rem(40px);
      margin-bottom: $wds-spacing-s;
    }

    .sh-tile-title {
      @include body-2-bld;

      line-break: auto;
      hyphens: auto;
    }

    .sh-tile-value {
      @include title-3-bld;

      line-break: auto;
      hyphens: auto;
    }

    &:active,
    &:focus {
      text-decoration: none;
    }

    &.most-booked {
      color: $wds-color-red-dark;
      background-color: $wds-color-red-lightest;

      &:focus-visible {
        outline: $wds-spacing-xxs solid $wds-color-ink-darker !important;
        outline-offset: $wds-spacing-xxs;
      }
    }

    &.travellers-staying {
      color: $wds-color-sunset-dark;
      background-color: $wds-color-sunset-lightest;
    }

    &.social-members {
      color: $wds-color-green-dark;
      background-color: $wds-color-green-lightest;
      grid-column: 1 / span 2;
      grid-row: 2;

      @include inline-tile;

      grid-template-columns: wds-rem(40px) auto;
      place-content: center;
      width: 100%;

      .sh-tile-icon {
        filter: saturate(0.4) hue-rotate(255deg) brightness(1.5) contrast(1.25);
      }
    }

    &.travellers-in-chat {
      color: $wds-color-purple-dark;
      background-color: $wds-color-purple-lightest;

      .sh-tile-icon {
        filter: saturate(0.8) hue-rotate(210deg);
      }
    }

    &.travellers-in-events {
      color: $wds-color-blue-dark;
      background-color: $wds-color-blue-lightest;

      .sh-tile-icon {
        filter: saturate(0.45) hue-rotate(185deg);
      }
    }
  }

  > .social-highlight-button {
    width: 100%;
    grid-row: 4;
    grid-column: 1 / span 2;
  }

  @include tablet-large {
    gap: $wds-spacing-m;
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: 1fr min-content min-content;

    .social-highlight-tile {
      padding: $wds-spacing-m;
      grid-column: auto / span 2;

      &.social-members {
        grid-column: auto / span 2;
        grid-row: auto;

        @include block-tile;
      }

      &.travellers-in-chat {
        grid-column: auto / span 3;

        @include inline-tile;
      }

      &.travellers-in-events {
        grid-column: auto / span 3;

        @include inline-tile;
      }
    }

    .social-highlight-button {
      width: auto;
      grid-row: 3;
      grid-column: 1 / span 6;
      place-self: center end;
    }
  }

  @include desktop {
    .social-highlight-tile {
      &.most-booked,
      &.travellers-staying,
      &.social-members,
      &.travellers-in-chat,
      &.travellers-in-events {
        @include inline-tile;
      }
    }
  }

  @media screen and (min-width: wds_em(1280px)) {
    grid-template-columns: repeat(5, 1fr);
    grid-template-rows: 1fr min-content;

    .social-highlight-tile {
      &.most-booked,
      &.travellers-staying,
      &.social-members,
      &.travellers-in-chat,
      &.travellers-in-events {
        grid-column: auto;
        grid-row: auto;
        align-content: center;

        @include inline-tile;
      }
    }

    .social-highlight-button {
      width: auto;
      grid-row: 2;
      grid-column: 1 / span 5;
      place-self: center end;
    }
  }
}
</style>
